import { MeetingOrderFilterType } from "../../../../shared/components/filters/orderTypeFilter/helpers"
import type { Organizer } from "../../../../shared/types/OrganizerTypes"
import type { MonetaryAmount } from "../../../currency/currencyTypes"
import type { DeliveryType } from "../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import type { MeetingRowOrder } from "../../../nonAdminUser/reports/home/weekMenuTypes"
import type { Resource } from "../../../nonAdminUser/reports/upcomingOrders/resourceBookings/userResourceBookingTypes"

type UpcomingMeetingDetails = Readonly<{
    meetingName: string
    startDateTime: string
    endDateTime: string
}>

type UpcomingResourceBookingOrder = Readonly<{
    id: number
    organizers: Organizer[]
    bookingDetails: Readonly<{ resources: Resource[] }>
    meetingDetails: UpcomingMeetingDetails & Readonly<{ location?: string }>
}>

type UpcomingMeetingServiceOrder = Readonly<{
    id: number
    uid: string
    orderType: string
    organizers: Organizer[]
    amount?: MonetaryAmount
    deliveryDetails?: Readonly<{
        time?: string
        type?: DeliveryType
        location?: Readonly<{ name: string }>
        isLocked?: boolean
        sittings?: number
    }>
    meetingDetails: UpcomingMeetingDetails & Readonly<{ location?: Readonly<{ name: string }> }>
}>

export type UpcomingMeetingOrder = UpcomingResourceBookingOrder | UpcomingMeetingServiceOrder

export type UpcomingMeetingsResponse = Readonly<{
    orders: UpcomingMeetingOrder[]
}>

export type UpcomingMeetingRow = MeetingRowOrder &
    Readonly<{ orderKind: MeetingOrderFilterType.RESOURCE_BOOKING | MeetingOrderFilterType.MEETING_SERVICE }>

// Only resource bookings carry bookingDetails; meeting service orders carry deliveries and an orderType.
function isResourceBookingOrder(order: UpcomingMeetingOrder): order is UpcomingResourceBookingOrder {
    return "bookingDetails" in order
}

// The endpoint omits orderSummary, but MeetingRow needs it to tell a booking from a catering order.
export function toUpcomingMeetingRow(order: UpcomingMeetingOrder): UpcomingMeetingRow {
    const { id, organizers, meetingDetails } = order
    const { meetingName, startDateTime, endDateTime } = meetingDetails

    if (isResourceBookingOrder(order)) {
        return {
            id,
            organizers,
            uid: "",
            orderKind: MeetingOrderFilterType.RESOURCE_BOOKING,
            orderSummary: { participants: 0, hasBooking: true, hasCatering: false, hasAudioVideoEquipment: false },
            meetingDetails: { meetingName, startDateTime, endDateTime, location: order.meetingDetails.location },
            deliveryDetails: {},
        }
    }

    return {
        id,
        organizers,
        uid: order.uid,
        orderKind: MeetingOrderFilterType.MEETING_SERVICE,
        orderSummary: { participants: 0, hasBooking: false, hasCatering: true, hasAudioVideoEquipment: false },
        meetingDetails: { meetingName, startDateTime, endDateTime, location: order.meetingDetails.location?.name },
        amount: order.amount,
        deliveryDetails: order.deliveryDetails ?? {},
    }
}
